import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Plane, ShieldCheck, AlertTriangle, Radar, ExternalLink,
  Clock3, BadgeCheck, Route, Wallet,
} from "lucide-react";
import { Shell } from "../components/Shell";
import { eur } from "../lib/api";
import { loadSelectedXpPlan, saveXpWatch } from "../lib/xpPlan";
import { useI18n } from "../i18n";
import { toast } from "sonner";

const fmtDateTime = (value, lang) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(lang === "fr" ? "fr-FR" : "en-GB", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch { return value; }
};

const carrierLabel = (segment) =>
  segment?.marketing_carrier ||
  segment?.operating_carrier ||
  segment?.marketing_carrier_iata ||
  segment?.operating_carrier_iata ||
  "—";

export default function XPDecision() {
  const navigate = useNavigate();
  const { t, lang } = useI18n();
  const selected = loadSelectedXpPlan();
  const plan = selected?.plan;
  const context = selected?.context || {};

  if (!plan) {
    return (
      <Shell active="plan">
        <button onClick={() => navigate("/loyalty")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 mb-5">
          <ArrowLeft size={16} /> {t("loyaltyDecision.back")}
        </button>
        <div className="dv-card p-8">
          <div className="font-display font-bold text-2xl">{t("loyaltyDecision.notFound")}</div>
        </div>
      </Shell>
    );
  }

  const watch = () => {
    saveXpWatch(plan, context);
    toast.success(t("loyaltyDecision.watchSaved"));
    navigate("/watch");
  };

  return (
    <Shell active="plan">
      <button onClick={() => navigate("/loyalty")} className="inline-flex items-center gap-1.5 text-sm text-slate-500 mb-5 hover:text-slate-900">
        <ArrowLeft size={16} /> {t("loyaltyDecision.back")}
      </button>

      <div className="text-[11px] font-semibold tracking-[0.24em] font-display" style={{ color: "var(--brand)" }}>
        {t("loyaltyDecision.kicker")}
      </div>
      <div className="mt-2 flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">{t("loyaltyDecision.title")}</h1>
          <p className="mt-2 text-sm text-slate-500 max-w-2xl">{t("loyaltyDecision.sub")}</p>
        </div>
        <div className="text-left lg:text-right">
          <div className="dv-total text-3xl">{eur(plan.total_cost)}</div>
          <div className="text-sm font-semibold" style={{ color: "var(--book-ink)" }}>+{plan.xp_total} XP {t("loyalty.projected")}</div>
        </div>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <Metric icon={Wallet} label={t("loyaltyDecision.totalCost")} value={eur(plan.total_cost)} />
        <Metric icon={BadgeCheck} label={t("loyaltyDecision.xp")} value={`${plan.xp_total} / ${context.target_xp || plan.target_xp || "—"} XP`} />
        <Metric icon={Route} label={t("loyaltyDecision.efficiency")} value={plan.eur_per_xp != null ? `${plan.eur_per_xp} €/XP` : "—"} />
        <Metric icon={Clock3} label={t("loyaltyDecision.completion")} value={plan.completion_date || "—"} />
      </div>

      {plan.requires_manual_validation && (
        <div className="mt-4 rounded-2xl p-4 flex gap-3" style={{ background: "var(--wait-soft)" }}>
          <AlertTriangle size={18} color="var(--wait-ink)" className="shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold" style={{ color: "var(--wait-ink)" }}>{t("loyalty.manualValidation")}</div>
            <div className="text-sm text-slate-600 mt-1">{t("loyaltyDecision.manualNote")}</div>
          </div>
        </div>
      )}

      <div className="mt-6 space-y-4">
        {(plan.runs || []).map((run, runIndex) => (
          <div key={run.offer_id || run.source_offer_id || runIndex} className="dv-card p-5 sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div>
                <div className="text-[11px] text-slate-400 font-semibold tracking-[0.18em]">{t("loyaltyDecision.run", { n: runIndex + 1 })}</div>
                <div className="mt-1 font-display font-bold text-xl">{run.origin} → {run.destination} → {run.origin}</div>
                <div className="text-sm text-slate-500 mt-1">{run.departure_date} → {run.return_date}</div>
              </div>
              <div className="text-right">
                <div className="font-display font-bold text-lg">+{run.xp_total} XP</div>
                <div className="text-sm text-slate-500">{eur(run.flight_total)}</div>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              {(run.segments || []).map((segment, i) => (
                <div key={`${segment.origin}-${segment.destination}-${segment.flight_number || i}`} className="rounded-2xl p-4" style={{ background: "var(--paper)", border: "1px solid var(--line)" }}>
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--brand-soft)" }}>
                        <Plane size={16} color="var(--brand)" />
                      </div>
                      <div>
                        <div className="font-display font-bold">{segment.origin} → {segment.destination}</div>
                        <div className="text-sm text-slate-700 mt-0.5">
                          {carrierLabel(segment)}
                          {segment.marketing_carrier_iata && ` · ${segment.marketing_carrier_iata}${segment.flight_number || ""}`}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          {fmtDateTime(segment.departing_at, lang)} → {fmtDateTime(segment.arriving_at, lang)}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-semibold" style={{ color: "var(--book-ink)" }}>+{segment.xp ?? "—"} XP</div>
                      <div className="text-xs text-slate-500">{segment.cabin_marketing_name || segment.cabin || "—"}</div>
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2 text-[11px] text-slate-600">
                    {segment.fare_basis_code && <span className="dv-pill px-2 py-1">{t("loyaltyDecision.fareBasis")}: {segment.fare_basis_code}</span>}
                    {segment.flight_type && <span className="dv-pill px-2 py-1">{segment.flight_type}</span>}
                    {segment.distance_miles != null && <span className="dv-pill px-2 py-1">{Math.round(segment.distance_miles)} mi</span>}
                  </div>

                  {segment.requires_manual_validation && (
                    <div className="mt-3 text-xs flex items-center gap-2" style={{ color: "var(--wait-ink)" }}>
                      <ShieldCheck size={13} /> {t("loyaltyDecision.segmentValidation")}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 dv-card p-5">
        <div className="font-display font-bold text-lg">{t("loyaltyDecision.nextTitle")}</div>
        <p className="mt-1 text-sm text-slate-500">{t("loyaltyDecision.nextBody")}</p>
        <div className="mt-4 flex flex-wrap gap-3">
          <button onClick={watch} className="dv-btn dv-btn-primary px-5 py-3" data-testid="xp-watch-button">
            <Radar size={16} /> {t("loyaltyDecision.watch")}
          </button>
          <button disabled className="dv-btn dv-btn-ghost px-5 py-3 opacity-50 cursor-not-allowed" title={t("loyaltyDecision.bookingUnavailable")}>
            <ExternalLink size={16} /> {t("loyaltyDecision.book")}
          </button>
        </div>
        <div className="mt-3 text-xs text-slate-400">{t("loyaltyDecision.bookingUnavailable")}</div>
      </div>
    </Shell>
  );
}

const Metric = ({ icon: Icon, label, value }) => (
  <div className="dv-card-flat p-4">
    <div className="flex items-center gap-2 text-xs text-slate-500"><Icon size={14} color="var(--brand)" /> {label}</div>
    <div className="mt-2 font-display font-bold text-lg">{value}</div>
  </div>
);
